import { ArrowBigDownDash, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { parsePhone } from "../../../utils/common";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";

const AddLeadForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandForm, setExpandForm] = useState(false); // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  const [addContacts, setAddContacts] = useState(false);
  const [formData, setFormData] = useState({
    source: "",
    address: "",
    owners: [
      {
        firstName: "",
        lastName: "",
        email: "",
        phones: "",
        backupPhones: "",
      },
    ],
    add_contacts: "",
    refLeadId: "",
    refLeadIdentifier: "",
    refLeadType: "",
    apn: "",
    skip_market_score: false,
  });

  useEffect(() => {
    const leadParam = searchParams.get("lead");

    if (leadParam) {
      try {
        const parsedLead = JSON.parse(leadParam);

        let sourceValue = "";
        const t = parsedLead?.refLeadType?.toLowerCase();

        if (t === "lead-s") sourceValue = "S";
        else if (t === "lead-m") sourceValue = "M";
        else if (t === "lead-b") sourceValue = "B";
        else if (t === "lead-d") sourceValue = "D";
        else sourceValue = "";

        setFormData({
          source: sourceValue,
          address: parsedLead.address || "",
          owners: (parsedLead.owners || []).map((o) => ({
            firstName: o.firstName || "",
            lastName: o.lastName || "",
            email: o.email || "",
            phones: o.phones || "",
            backupPhones: o.backupPhones || "",
          })),
          add_contacts: parsedLead.add_contacts || undefined,
          refLeadId: parsedLead.refLeadId || undefined,
          refLeadIdentifier: parsedLead.refLeadIdentifier || undefined,
          refLeadType: parsedLead.refLeadType || undefined,
          apn: parsedLead.apn || undefined,
        });
        setAddContacts(parsedLead.add_contacts);
        setExpandForm(true);
      } catch (err) {
        console.error("Invalid lead data", err);
      }
    }
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOwnerChange = (ownerIndex, field, value) => {
    setFormData((prev) => {
      const updatedOwners = [...prev.owners];
      updatedOwners[ownerIndex][field] = value;
      return { ...prev, owners: updatedOwners };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.address.trim()) {
      toast.error("Please enter a full address.");
      return;
    }

    // Validate owners
    for (let i = 0; i < formData.owners.length; i++) {
      const owner = formData.owners[i];
      const hasAnyField = owner.firstName.trim() || owner.lastName.trim() || owner.email.trim() || owner.phones.trim();

      if (hasAnyField) {
        if (!owner.firstName.trim()) {
          toast.error(`Owner ${i + 1}: First Name is required.`);
          return;
        }
        if (!owner.lastName.trim()) {
          toast.error(`Owner ${i + 1}: Last Name is required.`);
          return;
        }
        if (!owner.phones.trim()) {
          toast.error(`Owner ${i + 1}: At least one phone is required.`);
          return;
        }

        const phoneLines = owner.phones
          .split("\n")
          .map((p) => p.trim())
          .filter((p) => p !== "");

        for (let phoneLine of phoneLines) {
          const parsed = parsePhone(phoneLine);

          if (!parsed || !parsed.phone) {
            toast.error(`Owner ${i + 1}: Phone "${phoneLine}" is invalid (must contain 10 digits).`);
            return;
          }
        }
      }
    }

    try {
      setLoading(true);
      const { address, owners, ...restFields } = formData;
      const payload = {
        full_address: address,
        owners: owners
          .filter((o) => o.firstName?.trim() || o.lastName?.trim() || o.email?.trim() || o.phones?.trim())
          .map((o) => {
            const phoneLines = (o.phones || "")
              .split("\n")
              .map((p) => p.trim())
              .filter(Boolean);

            const backupPhoneLines = (o.backupPhones || "")
              .split("\n")
              .map((p) => p.trim())
              .filter(Boolean);

            const parsedPhones = phoneLines
              .map(parsePhone)
              .filter((p) => p?.phone)
              .map((p) => ({
                ...p,
                is_backup: false,
              }));

            const backupParsedPhones = backupPhoneLines
              .map(parsePhone)
              .filter((p) => p?.phone)
              .map((p) => ({
                ...p,
                is_backup: true,
              }));

            return {
              first_name: o.firstName?.trim(),
              last_name: o.lastName?.trim(),
              email: o.email?.trim(),
              phones: [...parsedPhones, ...backupParsedPhones],
            };
          }),
        ...restFields,
      };

      //   console.log({ formData, payload });
      const resp = await axiosInstance.post(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/lead`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = resp.data;

      if (data.success) {
        toast.success("Lead added successfully!");
        setFormData({
          address: "",
          owners: [{ firstName: "", lastName: "", email: "", phones: "", backupPhones: "" }],
        });
      } else {
        toast.error(data.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add lead. Please try again.");
      console.error("Failed to add lead. Please try again:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="input_fields_container">
      <form className="w-full grid md:grid-cols-3 gap-4" onSubmit={handleSubmit}>
        <div className="!flex !items-center !justify-center md:col-span-3">
          <div className="!w-full flex item-center !space-x-2">
            <input type="text" name="address" className="input_field focus:!outline-none w-full" placeholder="Address..." value={formData.address} onChange={(e) => handleChange("address", e.target.value)} />
            <select className="input_field focus:!outline-none !w-24 bg-white cursor-pointer " value={formData.source} onChange={(e) => handleChange("source", e.target.value)}>
              <option value="">Auto</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="B">B</option>
              <option value="D">D</option>
            </select>
          </div>
          <div className="!px-2 !pb-2">
            <ArrowBigDownDash size={24} className={`transition-transform duration-300 cursor-pointer flex justify-center item-center hover:text-blue-800 ${expandForm ? "rotate-180" : "rotate-0"}`} onClick={() => setExpandForm((prev) => !prev)} />
          </div>
        </div>
        <div className="flex justify-center item-start w-full md:col-span-3 ">
          <div>
            <button
              type="button"
              className={`overflow-hidden bg-[#008080] text-white rounded hover:bg-[#009999] cursor-pointer shadow transition-all duration-500 ${expandForm ? "scale-100 mt-4 mr-3 !p-1 h-min " : "scale-0 h-0 w-0"}`}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  owners: [...prev.owners, { firstName: "", lastName: "", email: "", phones: "", backupPhones: "" }],
                }))
              }
            >
              <Plus size={20} />
            </button>
          </div>
          <div className={`w-full transition-all duration-500 !pr-5 ${expandForm ? "max-h-[300px] opacity-100 mt-4 overflow-y-auto " : "max-h-0 opacity-0 overflow-hidden "}`}>
            {formData.owners.map((owner, i) => (
              <div key={i} className="!flex !items-center !justify-center !mb-2">
                <div className="!w-full rounded-xl !pr-5 flex item-start">
                  <div className="w-full">
                    <div className="flex flex-wrap flex-col md:flex-row gap-2 items-center mb-2">
                      <input type="text" placeholder="First Name" className="input_field flex-1 outline-none" value={owner.firstName} onChange={(e) => handleOwnerChange(i, "firstName", e.target.value)} />
                      <input type="text" placeholder="Last Name" className="input_field flex-1 outline-none" value={owner.lastName} onChange={(e) => handleOwnerChange(i, "lastName", e.target.value)} />
                      <input type="email" placeholder="Email" className="input_field flex-1 outline-none" value={owner.email} onChange={(e) => handleOwnerChange(i, "email", e.target.value)} />
                      <input type="checkbox" checked={formData.skip_market_score} onChange={(e) => handleChange("skip_market_score", e.target.checked)} className="ml-2 w-4 h-4 cursor-pointer" />
                      {/* <label className="text-sm text-gray-600 cursor-pointer ml-1">Skip Market Score?</label> */}
                    </div>
                    <textarea name="phones" id="phones" placeholder="Phones..." rows={5} className="input_field flex-1 bg-white outline-none" value={owner.phones} onChange={(e) => handleOwnerChange(i, "phones", e.target.value)}></textarea>
                    <textarea name="backupPhones" id="backupPhones" placeholder="Backup Phones..." rows={5} className="input_field flex-1 bg-white outline-none mt-2" value={owner.backupPhones} onChange={(e) => handleOwnerChange(i, "backupPhones", e.target.value)}></textarea>
                  </div>
                  {formData.owners.length > 1 && (
                    <button
                      type="button"
                      className="h-min !mt-3 !ml-3 bg-red-700 text-white !p-1 rounded hover:bg-red-800 hover:text-white cursor-pointer shadow"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          owners: prev.owners.filter((_, idx) => idx !== i),
                        }))
                      }
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full md:col-span-3">
          <button className="w-full !max-w-xs text-white cursor-pointer text-xl !px-6 !py-2 rounded-sm !bg-[#008080] hover:!bg-[#007070]" type="submit" disabled={loading}>
            {loading ? "Adding..." : addContacts ? "Add Contacts" : "Add Lead"}
          </button>
        </div>

        {error && <div style={{ color: "red", gridColumn: "1 / -1" }}>{error}</div>}
      </form>
    </div>
  );
};

export default AddLeadForm;
